/**
 * ============================================================
 * persistence.ts — Helpers de Persistencia para Zustand Store
 * ============================================================
 *
 * Responsabilidade: Fornecer funcoes utilitarias para persistir e
 *   recuperar estado do Zustand no localStorage, com versionamento
 *   para migracao segura entre versoes do schema.
 *
 * Quando usar: Em qualquer slice do Zustand que precise persistir
 *   estado entre sessoes (tema, colunas, roles, IDs ativos).
 *
 * Comportamentos-chave:
 *   - Versionamento: cada valor salvo inclui `_version`. Se a versao
 *     armazenada diferir da STORE_VERSION atual, o dado e descartado
 *     e o fallback e retornado (migracao implicita).
 *   - Falha silenciosa: erros de parsing ou quota exceeded sao
 *     engolidos — localStorage e complementar, nao critico.
 *
 * @example
 *   const theme = loadPersisted<'light' | 'dark'>('bl-theme', 'light');
 *   persist('bl-theme', 'dark');
 * ============================================================
 */

/** Versao do schema de persistencia — incrementar ao mudar estrutura dos dados salvos */
const STORE_VERSION = 1;

/**
 * Carrega um valor persistido do localStorage com verificacao de versao.
 *
 * @param key - Chave no localStorage
 * @param fallback - Valor padrao retornado se nao existir, versao incompativel, ou erro de parse
 * @returns O valor persistido ou o fallback
 */
export function loadPersisted<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const v = localStorage.getItem(key);
    if (!v) return fallback;
    const parsed = JSON.parse(v);

    if (parsed && typeof parsed === 'object' && '_version' in parsed) {
      if (parsed._version !== STORE_VERSION) {
        localStorage.removeItem(key);
        return fallback;
      }
      return parsed.data !== undefined ? parsed.data : fallback;
    } else {
      localStorage.removeItem(key);
      return fallback;
    }
  } catch {
    return fallback;
  }
}

/**
 * Persiste um valor no localStorage com envelope de versao.
 * Falha silenciosa em caso de quota exceeded ou localStorage indisponivel.
 *
 * @param key - Chave no localStorage
 * @param value - Valor a ser serializado e salvo
 */
export function persist<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify({ _version: STORE_VERSION, data: value }));
  } catch { /* quota exceeded or unavailable */ }
}
